import React from 'react'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import { useStableValue } from '@/hooks/use-shallow-stable-value'
import { logger } from '@/utils/logger'
import { sameSelectionIgnoringPosition } from './helpers/same-selection-ignoring-position'
import type {
  ActiveObject,
  ActiveObjectContextValue,
  ActiveObjects,
} from './types/active-object-context-value'
import type { ActiveObjectActions, ActiveSelection } from './types/active-selection'

/** Everything, live: changes on every frame while a drag moves the selection. */
export const ActiveObjectContext = React.createContext<ActiveObjectContextValue | null>(null)
/** The selection without live position; see `ActiveSelection`. */
export const ActiveSelectionContext = React.createContext<ActiveSelection | null>(null)
/** The setters alone; never changes. */
export const ActiveObjectActionsContext = React.createContext<ActiveObjectActions | null>(null)

function sameActiveSelection(a: ActiveSelection, b: ActiveSelection): boolean {
  return (
    sameSelectionIgnoringPosition(a.activeObjects, b.activeObjects) &&
    sameSelectionIgnoringPosition(a.activeTextBoxes, b.activeTextBoxes)
  )
}

export function ActiveObjectProvider({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement {
  const [activeObjects, setActiveObjectsRaw] = React.useState<ActiveObjects>([])
  const [activeTextBoxes, setActiveTextBoxes] = React.useState<TextBoxData[]>([])
  const [overflowingTextBoxIds, setOverflowingTextBoxIds] = React.useState<Set<string>>(
    () => new Set()
  )
  const activeObjectsRef = React.useRef<ActiveObjects>(activeObjects)

  activeObjectsRef.current = activeObjects

  const activeObject = activeObjects.length > 0 ? activeObjects[0] : null

  const setActiveObject = React.useCallback((obj: ActiveObject) => {
    if (process.env.NODE_ENV !== 'production' && obj !== null) {
      const prev = activeObjectsRef.current

      if (prev.length > 1) {
        logger.warn(
          `[ActiveObjectContext] setActiveObject called while a ${prev.length}-object multi-selection ` +
            'was active -- this collapses the selection to 1 item. Use setActiveObjects instead if you ' +
            'need to refresh an entry without altering selection size.'
        )
      }
    }

    setActiveObjectsRaw(obj ? [obj] : [])
  }, [])

  const setActiveObjects = React.useCallback((objs: ActiveObjects) => {
    setActiveObjectsRaw(objs)
  }, [])

  const setTextBoxOverflow = React.useCallback((textBoxId: string, isOverflowing: boolean) => {
    setOverflowingTextBoxIds((prev) => {
      // Bail out (same ref → no re-render) unless membership actually changes, so a box that stays
      // overflowing across keystrokes never re-renders the panels.
      if (isOverflowing === prev.has(textBoxId)) return prev

      const next = new Set(prev)

      if (isOverflowing) {
        next.add(textBoxId)
      } else {
        next.delete(textBoxId)
      }

      return next
    })
  }, [])

  const value = React.useMemo(
    () => ({
      activeObject,
      setActiveObject,
      activeObjects,
      setActiveObjects,
      activeTextBoxes,
      setActiveTextBoxes,
      overflowingTextBoxIds,
      setTextBoxOverflow,
    }),
    [
      activeObject,
      setActiveObject,
      activeObjects,
      setActiveObjects,
      activeTextBoxes,
      overflowingTextBoxIds,
      setTextBoxOverflow,
    ]
  )

  const selection = useStableValue<ActiveSelection>(
    { activeObject, activeObjects, activeTextBoxes },
    sameActiveSelection
  )

  const actions = React.useMemo<ActiveObjectActions>(
    () => ({ setActiveObject, setActiveObjects, setActiveTextBoxes, setTextBoxOverflow }),
    [setActiveObject, setActiveObjects, setTextBoxOverflow]
  )

  return (
    <ActiveObjectActionsContext.Provider value={actions}>
      <ActiveSelectionContext.Provider value={selection}>
        <ActiveObjectContext.Provider value={value}>{children}</ActiveObjectContext.Provider>
      </ActiveSelectionContext.Provider>
    </ActiveObjectActionsContext.Provider>
  )
}
